/// <amd-module name="Repos/Views/Import/Components/EmptyRepoView/EmptyRepoView" />
import * as React from "react";

import { GitAsyncOperationStatus, GitImportRequest } from "Repos/Common/Contracts/Git";
import { IProjectRepositoryInformationService } from "Repos/Common/ProjectRepositoryInformationService";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { GettingStarted } from "Repos/Views/Import/GettingStarted";
import { ImportProgress } from "Repos/Views/Import/ImportProgress";
import * as Resources from "Repos/Views/Import/Resources";
import { getTFSProject } from "Tfs/Platform/Page";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { PerformanceScenario } from "VSS/Platform/Components/Performance";
import { onClickFPS } from "VSS/Platform/FPS";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Observer } from "VSSUI/Observer";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";

class EmptyRepository extends VssComponent<{}> {
    private importRequest = new ObservableValue<GitImportRequest | undefined>(undefined);
    private notFoundRepoName: string | undefined;
    private disabledRepoName: string | undefined;

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        // Track any active import request we have for this repository.
        const vcViewModelDataProviderService = context.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const vcViewModel = vcViewModelDataProviderService.getViewModel();
        this.notFoundRepoName = vcViewModel.notFoundRepoName;
        this.disabledRepoName = vcViewModel.disabledRepoName;
        if (vcViewModel.activeImportRequest) {
            this.importRequest.value = vcViewModel.activeImportRequest;
        }
    }

    public render() {
        return (
            <PerformanceScenario
                area="repos"
                scenarioName={this.importRequest.value ? "repos-import-repository" : "repos-getting-started"}
                tti={true}
            >
                <Observer importRequest={this.importRequest}>
                    {(props: { importRequest: GitImportRequest | undefined }) => {
                        if (this.disabledRepoName) {
                            return (
                                <ZeroData
                                    className="flex-grow"
                                    imagePath={getAssetLocation(this.context.pageContext, "ms.vss-code-web/import-content/repoDisabled.svg")}
                                    imageAltText={Resources.RepositoryDisabled}
                                    primaryText={format(Resources.RepositoryDisabledTitleFormat, this.disabledRepoName)}
                                    secondaryText={Resources.RepositoryDisabledSecondaryText}
                                />
                            );
                        } else if (this.notFoundRepoName) {
                            const projectData = this.context.pageContext
                                .getService<IProjectRepositoryInformationService>("IProjectRepositoryInformationService")
                                .getData();

                            let href: string | undefined;
                            let actionText: string | undefined;
                            if (projectData && projectData.defaultGitRepoName) {
                                const routingService = this.context.pageContext.getService<IReposRoutingService>("IReposRoutingService");
                                href = routingService.getSwitchRepositoryURL(
                                    projectData.defaultGitRepoName,
                                    getTFSProject(this.context.pageContext)!.name
                                );
                                actionText = format(Resources.RepositoryNotFoundNavigateText, projectData.defaultGitRepoName);
                            }

                            return (
                                <ZeroData
                                    className="flex-grow"
                                    imagePath={getAssetLocation(this.context.pageContext, "ms.vss-code-web/import-content/repoNotFound.svg")}
                                    imageAltText={Resources.RepositoryNotFound}
                                    primaryText={format(Resources.RepositoryNotFoundTitleFormat, this.notFoundRepoName)}
                                    secondaryText={Resources.RepositoryNotFoundSecondaryText}
                                    actionType={ZeroDataActionType.ctaButton}
                                    actionText={actionText}
                                    actionHref={href}
                                    onActionClick={href ? event => onClickFPS(this.context.pageContext, href!, true, event) : undefined}
                                />
                            );
                        } else if (props.importRequest && props.importRequest.status !== GitAsyncOperationStatus.Abandoned) {
                            return <ImportProgress importRequest={props.importRequest} />;
                        } else {
                            return <GettingStarted importRequest={this.importRequest} />;
                        }
                    }}
                </Observer>
            </PerformanceScenario>
        );
    }
}

Components.add("ms.vss-code-web.empty-repo-view", EmptyRepository);
